/**
 * Helper: Personalisation
 * -----------------------------------------------------------------------------
 * Functions used for product personalisation.
 *
 */

import emojiRegex from 'emoji-regex'

/**
 * Check if string should be blocked.
 * - Splits string into individual words
 * - Checks each word to see if any are blocked
 * @param {String} string string to check
 * @returns {Boolean}
 */
export function checkStringForProfanity(string) {
  const words = string.toLowerCase().split(' ')

  const isAnyWordBlocked = words.some((word) => {
    const isBlockedWord = checkWordForBlocked(word)
    const isExceptionWord = checkWordForException(word)

    const isBlocked = isBlockedWord && !isExceptionWord

    return isBlocked
  })

  return isAnyWordBlocked
}

/**
 * Check if word is in the exceptions list.
 * @param {String} string string to check
 * @returns {Boolean}
 */
function checkWordForException(string) {
  const exceptionWords = window.cnvs?.personalisation?.profanityExceptions ?? []

  return exceptionWords.some((exceptionWord) => {
    return isStringAMatch(string, exceptionWord)
  })
}

/**
 * Check if word is in the blocked list.
 * @param {String} string string to check
 * @returns {Boolean}
 */
function checkWordForBlocked(string) {
  const blockedWords = window.cnvs?.personalisation?.profanity ?? []

  return blockedWords.some((blockedWord) => {
    return isStringAMatch(string, blockedWord)
  })
}

/**
 * Check if a string matches a pattern.
 * - Pattern can be a standard string or include `*` charcters for wildcard.
 *   - `*` characters are only included if used at the start or the end of the
 *     string and are ignored if used in the middle to prevent over-matching.
 * - Examples:
 *   - 'foo', 'foo' -> match
 *   - 'foo*', 'foobar' -> match
 *   - '*bar', 'foobar' -> match
 *   - '*bar*', 'foobarbaz' -> match
 *   - 'bar', 'foo' -> no match
 *   - 'food', 'foo' -> no match
 *   - 'foobar', 'f**bar' -> no match (* are not at start or end so matches
 *     exactly)
 * @param {String} string - string to check
 * @param {String} pattern - pattern to check against.
 * @returns {Boolean}
 */
function isStringAMatch(string, pattern) {

  // Replace special regex characters in the pattern except for '*'
  const escapedPattern = pattern.replace(/(?:[.+?^=!:${}()|[\]/\\])/g, '')

  // Handle patterns with '*' at the start, end, or both
  if (escapedPattern.startsWith('*') || escapedPattern.endsWith('*')) {
    // Replace leading and trailing '*' for regex construction
    let regexPattern = escapedPattern

    if (escapedPattern.startsWith('*')) {
      regexPattern = regexPattern.replace(/^\*/, '.*')
    }
    if (escapedPattern.endsWith('*')) {
      regexPattern = regexPattern.replace(/\*$/, '.*')
    }
    const regex = new RegExp(`^${regexPattern}$`, 'i')

    const match = regex.test(string)

    return match
  }

  const match = string.toLowerCase() === pattern.toLowerCase()

  return match
}

/**
 * Set up emoji regex from 'emoji-regex' package.
 * - Exported so that it can easily be used across the codebase if needed.
 */
export const regexForEmoji = emojiRegex()

/**
 * Test a string to see if it contains an emoji.
 * @param {String} string string to test
 * @returns {Boolean} - whether the string contains an emoji.
 */
export function checkStringForEmoji(string) {
  return regexForEmoji.test(string)
}
